import React, { useState, useEffect, useCallback } from 'react';
import P, { InferProps } from 'prop-types';
import Typography from '@mui/material/Typography';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { SxStyle } from '../../../types';
import FormHelperText from '@mui/material/FormHelperText';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import LoadingButton from '@mui/lab/LoadingButton';
import firebaseApp from '../../../firebase-app';
import { useStringsContext } from '../../../contexts/strings';
import { useSnackbar } from 'notistack';
import { getMetadata, ref, uploadBytes } from 'firebase/storage';
import ImageDropzoneDialog from '../image-dropzone-dialog';

const styles: SxStyle = {
  card: {
    display: 'flex',
    alignItems: 'center',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    alignSelf: 'stretch',
    flex: 1,
  },
  image: {
    width: '100px',
    height: '100px',
    boxShadow: '0 2px 6px 0 rgba(0,0,0,0.24)',
    borderRadius: '1rem',
  },
  columnLabels: {
    marginLeft: '16px',
  },
  columnButtons: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    alignSelf: 'stretch',
  },
  compactButton: {
    minWidth: 'unset',
    padding: '8px',
    marginLeft: '16px',
    '& .MuiButton-startIcon': {
      marginRight: 0,
      marginLeft: 0,
    },
  },
  link: {
    marginTop: '6px',
  },
};

async function getImageSize(url: string): Promise<{ height: number; width: number }> {
  const img = new Image();
  img.src = url;
  return new Promise((resolve) => {
    img.onload = () => {
      resolve({
        height: img.height,
        width: img.width,
      });
    };
  });
}

function ImageField({
  path,
  field,
  widthField,
  heightField,
  setChange,
  isReadOnly,
  compact,
  imagePath,
}: InferProps<typeof ImageField.propTypes>) {
  const [imageUrl, setImageUrl] = useState<string>('');
  const [imageAlt, setImageAlt] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { strings } = useStringsContext();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setImageUrl(field.value);
    setImageAlt('');
  }, [field]);

  const onFileSelect = useCallback(
    async (file: any) => {
      const id = Date.now().toString(36);
      const fileName = file?.name.replace(/[^a-zA-Z0-9-_.]/g, '');
      const uniqueFilename = `${id}-${fileName}`;
      const storageRef = ref(
        firebaseApp.storage,
        `${imagePath || `teamImages/${path}`}/${uniqueFilename}`
      );

      try {
        setIsSubmitting(true);
        await uploadBytes(storageRef, file);
        // Use url pointing to the Google cloud storage bucket instead of Firebase storage (due to problem with url together with CDN)
        const fileMetaData = await getMetadata(storageRef);
        const url = `https://storage.googleapis.com/${
          process.env.REACT_APP_FIREBASE_STORAGEBUCKET
        }/${encodeURIComponent(fileMetaData.fullPath)}`;
        const { height, width } = await getImageSize(url);

        setImageUrl(url);
        setImageAlt(fileName);
        if (setChange) {
          setChange(field.key, url, field.validation, field.isTeamDataValue);

          if (widthField && heightField) {
            setChange(widthField.key, width, widthField.validation, widthField.isTeamDataValue);
            setChange(heightField.key, height, heightField.validation, heightField.isTeamDataValue);
          }
        }
        enqueueSnackbar(strings.messages.uploadSuccess, {
          variant: 'success',
        });
      } catch (error: any) {
        setErrorMessage(strings.messages.uploadErrorImage);
        enqueueSnackbar(strings.messages.uploadError, {
          variant: 'error',
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [field, heightField, path, imagePath, setChange, strings, widthField, enqueueSnackbar]
  );

  const onRemoveImage = useCallback(() => {
    setImageUrl('');
    setImageAlt('');

    if (setChange) {
      setChange(field.key, '', field.validation, field.isTeamDataValue);
    }
  }, [field, setChange]);

  return (
    <>
      <Box sx={styles.card}>
        <Avatar variant="square" src={imageUrl} alt={field.title} sx={styles.image} />
        <Box sx={styles.actions}>
          {(!compact || field.title.length === 0) && (
            <Box sx={styles.columnLabels}>
              <Typography variant="subtitle2">{field.title}</Typography>
              <Typography variant="caption">{imageAlt}</Typography>
            </Box>
          )}
          <Box sx={styles.columnButtons}>
            <LoadingButton
              sx={compact ? styles.compactButton : {}}
              disabled={field.key === 'Not available' || !!isReadOnly}
              variant="contained"
              color="primary"
              loading={isSubmitting}
              onClick={() => setOpen(true)}
              startIcon={<CloudUploadIcon />}
            >
              {compact ? '' : strings.actions.uploadImage}
            </LoadingButton>
            {imageUrl && (
              <Button
                sx={compact ? styles.compactButton : {}}
                onClick={onRemoveImage}
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                disabled={(isReadOnly ?? false) || isSubmitting}
              >
                {compact ? '' : strings.actions.removeImage}
              </Button>
            )}
          </Box>
        </Box>
        <ImageDropzoneDialog
          open={open}
          onClose={() => setOpen(false)}
          onSave={(files) => {
            onFileSelect(files[0]);
            setOpen(false);
          }}
        />
      </Box>
      {!compact && <FormHelperText error>{errorMessage}</FormHelperText>}
    </>
  );
}

ImageField.propTypes = {
  path: P.string,
  imagePath: P.string,
  setChange: P.func,
  isReadOnly: P.bool,
  compact: P.bool,
  field: P.shape({
    title: P.string.isRequired,
    id: P.string.isRequired,
    type: P.string,
    validation: P.any.isRequired,
    key: P.string,
    value: P.string.isRequired,
    isTeamDataValue: P.bool,
  }).isRequired,
  widthField: P.shape({
    title: P.string.isRequired,
    id: P.string.isRequired,
    type: P.string,
    validation: P.any.isRequired,
    key: P.string,
    value: P.oneOfType([P.string, P.number]).isRequired,
    isTeamDataValue: P.bool,
  }),
  heightField: P.shape({
    title: P.string.isRequired,
    id: P.string.isRequired,
    type: P.string,
    validation: P.any.isRequired,
    key: P.string,
    value: P.oneOfType([P.string, P.number]).isRequired,
    isTeamDataValue: P.bool,
  }),
};

export default React.memo(ImageField);
